import React from 'react';
import useClassNames from '@medflex/hooks/useClassNames';
import * as PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import copy from 'copy-to-clipboard';
import Logger from '@medflex/utils/Logger';
import Button from '../Button';
import { IconCopy, IconShareAlt } from '../Icons';

const PermanentLink = ({
  className,
  uri,
  onClickGenerateNew,
  onCopied,
  onShared,
  children,
  subTitle,
  canReset,
}) => {
  const cn = useClassNames(() => ['flex', 'flex-col', 'max-h-full', className], [className]);
  const { t } = useTranslation('components');

  const copyLink = event => {
    copy(uri);
    onCopied(event);
  };

  const shareLink = async event => {
    try {
      const shareData = {
        title: t('PermanentLink.share_title'),
        text: t('PermanentLink.share_text'),
        url: uri,
      };
      await navigator.share(shareData);
      onShared(event);
    } catch (e) {
      Logger.error(e);
    }
  };

  return (
    <div data-component="PermanentLink" className={cn}>
      <div className="text-slate-600">{subTitle}</div>
      <div className="lg:hidden text-slate-600 mt-4 mb-4 text-sm">{t('PermanentLink.text')}</div>
      <div className="hidden lg:flex items-center mb-2">
        <p className="border border-slate-300 w-full h-12 flex items-center my-1 mr-1 px-2 rounded bg-slate-100 truncate hover:underline text-slate-500">
          {uri}
        </p>
        <Button onClick={copyLink} className="flex-shrink-0" kind="primary:outline">
          {t('PermanentLink.copy')}
        </Button>
      </div>
      <div className="flex items-center lg:hidden mb-4 space-x-8">
        <button
          onClick={copyLink}
          type="button"
          className="focus:outline-none flex items-center text-blue-500 space-x-2"
        >
          <IconCopy size="5" className="fill-current" />
          <div>{t('PermanentLink.copy')}</div>
        </button>
        {navigator.share && (
          <button
            onClick={shareLink}
            type="button"
            className="focus:outline-none flex items-center text-blue-500 space-x-2"
          >
            <IconShareAlt size="5" className="fill-current" />
            <div>{t('PermanentLink.share')}</div>
          </button>
        )}
      </div>
      {canReset && (
        <div>
          <button
            onClick={onClickGenerateNew}
            type="button"
            className="block focus:outline-none text-blue-500 hover:underline mb-4"
          >
            {t('PermanentLink.generate_new')}
          </button>
        </div>
      )}

      {children}
    </div>
  );
};

PermanentLink.propTypes = {
  children: PropTypes.oneOfType([PropTypes.func, PropTypes.string, PropTypes.node]),
  onClickGenerateNew: PropTypes.func,
  uri: PropTypes.string,
  subTitle: PropTypes.string,
  onCopied: PropTypes.func,
  onShared: PropTypes.func,
  className: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.func,
    PropTypes.array,
    PropTypes.object,
  ]),
  canReset: PropTypes.bool,
};

PermanentLink.defaultProps = {
  onClickGenerateNew: () => {},
  onCopied: () => {},
  onShared: () => {},
  canReset: false,
};

export default PermanentLink;
